import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import { LanguageSettings } from '../models/language-settings.model';
import { Cookies } from '../shared/constants/cookie.constants';
import _ from 'lodash';
import { isValidLanguage } from '../utils/custom-validators';
import { APP } from '../shared/constants/constants';
import { environment } from 'src/environments/environment';
import { Dropdown } from '../models/dropdown.model';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  translateService = inject(TranslateService);
  cookieService = inject(CookieService);

  languageSubject = new BehaviorSubject<LanguageSettings | null>(null);
  public languageObs = this.languageSubject.asObservable();

  // e.g. in your service or a module initialization
  private languageMap = new Map<string, Dropdown>(
    APP.LANGUAGES.map(lang => [lang.value, lang])
  );

  getLanguageSettings(): LanguageSettings {
    let settings = new LanguageSettings();

    const appLang = this.cookieService.get(Cookies.LANG);

    if (!_.isEmpty(appLang)) {
      if (isValidLanguage(appLang)) {
        settings.appLocale = appLang;
      } else {
        settings.appLocale = APP.LANGUAGES[0].value;
      }
    }

    if (this.cookieService.check(Cookies.USER_LANG)) {
      const userLang = this.cookieService.get(Cookies.USER_LANG)
      if (!_.isEmpty(userLang)) {
        if (isValidLanguage(userLang)) {
          settings.userLocale = userLang;
        }
      }
    }

    return settings;
  }

  setLanguageSetting(setting: LanguageSettings): void {
    if (setting) {
      this.languageSubject.next(setting);
      return;
    }

    let settings = new LanguageSettings();
    this.languageSubject.next(settings);

  }

  /**
   * Gets the current language of the application
   * @returns 
   */
  getCurrentApplicationLanguage(): string {
    let lang = this.getLanguageSettings();

    return lang.appLocale;
  }

  /**
   * Get the logged in user's preferred language
   * @returns language, else null
   */
  getUserPreferredLanguage(): string | null {
    let lang = this.getLanguageSettings();

    return lang.userLocale ?? null;
  }

  getCurrentLanguage(): string {
    let lang = this.getLanguageSettings();

    if (lang.userLocale)
      return lang.userLocale;

    return lang.appLocale;
  }

  /**
 * Returns the index of a language in the APP.LANGUAGES array by its code.
 * @param code - The language code to look up (e.g. 'en', 'fr').
 * @returns The zero-based index of the language in APP.LANGUAGES, or -1 if not found.
 */
  getIndexOfLanguage(code: string): number {
    // Use Array.findIndex to scan through APP.LANGUAGES and
    // return the index of the first element whose `value` matches `code`.
    // findIndex stops as soon as it finds a match, so it's a single-pass operation.
    const idx = APP.LANGUAGES.findIndex(e => e.value === code);

    // If no match is found, findIndex returns -1–we just return that directly.
    return idx;
  }

  getSelectedLanguage(code: string): Dropdown {
    return this.languageMap.get(code) ?? APP.LANGUAGES[0];
  }

  // getSelectedLanguage(code: string): Dropdown {
  //   const found = APP.LANGUAGES.find(lang => lang.value === code);
  //   return found ?? APP.LANGUAGES[0];
  // }

  setLanguage(lang: string): void {
    this.translateService.use(lang);
  }

  setLoggedInUserLanguage(lang: string, expire: Date | null) {
    this.translateService.use(lang);
    this.cookieService.set(Cookies.USER_LANG, lang, {
      path: '/',
      domain: environment.cookieDomain,
      expires: expire ?? undefined
    });
  }

  setApplicationLanguage(lang: string) {
    this.cookieService.set(Cookies.LANG, lang, {
      path: '/',
      domain: environment.cookieDomain,
      expires: Cookies.LANG_EXPIRE_DAYS,
      secure: true,
      sameSite: 'None'
    });
  }

  initializeApplicationLanguage() {
    this.translateService.use(this.getCurrentApplicationLanguage());
  }

}